import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ExternalLink, FileText, ImageOff } from 'lucide-react';

/**
 * CertificateLightbox — readable enlarged viewer for a single canonical
 * corporate certificate. Backed by shadcn/Radix Dialog for a11y
 * (focus-trap, ESC close, `aria-modal`, `aria-labelledby`).
 *
 * Behaviour:
 *   • Image assets render inside a responsive frame (max 90vh) so text
 *     on the certificate stays legible on desktop and mobile.
 *   • PDF-only certificates surface a "Open PDF" affordance rather than
 *     silently attempting to embed a PDF.
 *   • Missing-asset case renders a plain-language empty state — never a
 *     broken image icon.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   certificate — canonical shape from GET /api/public/corporate-certificates:
 *     { public_title, description, issuing_authority, certificate_number,
 *       issue_date, valid_until, scope, status,
 *       asset_image_url, asset_pdf_url }
 *   labels     — resolved i18n strings (keeps this component locale-agnostic)
 */
const CertificateLightbox = ({ open, onClose, certificate, labels = {} }) => {
  if (!certificate) return null;

  const {
    public_title,
    description,
    issuing_authority,
    certificate_number,
    issue_date,
    valid_until,
    scope,
    status,
    asset_image_url,
    asset_pdf_url,
  } = certificate;

  const isInProgress = status === 'application_in_progress';
  const hasImage = !!asset_image_url;
  const hasPdf = !!asset_pdf_url;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        data-testid="certificate-lightbox"
        className="max-w-4xl w-[95vw] p-0 overflow-hidden bg-white sm:rounded-xl"
      >
        {/* Radix a11y — every DialogContent requires a DialogTitle for
            screen readers. We keep the visible `<h2>` as the design
            surface and hide a plain-text `DialogTitle` inside it so
            Radix's aria-labelledby wiring is satisfied without altering
            the visual layout. Same pattern for DialogDescription. */}
        <div className="flex flex-col max-h-[90vh]">
          <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle asChild>
                <h2
                  className="text-base sm:text-lg font-semibold text-slate-900 truncate"
                  data-testid="lightbox-title"
                >
                  {public_title || labels.untitled || 'Certificate'}
                </h2>
              </DialogTitle>
              {issuing_authority ? (
                <DialogDescription asChild>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {issuing_authority}
                  </p>
                </DialogDescription>
              ) : (
                <VisuallyHidden asChild>
                  <DialogDescription>
                    {isInProgress
                      ? (labels.pending || 'Application in progress')
                      : (public_title || 'Certificate details')}
                  </DialogDescription>
                </VisuallyHidden>
              )}
            </div>
            {isInProgress && (
              <span
                className="shrink-0 inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-xs font-medium"
                data-testid="lightbox-pending-badge"
              >
                {labels.pending || 'Application in progress'}
              </span>
            )}
          </div>

          <div className="flex-1 overflow-auto bg-slate-50">
            {hasImage ? (
              <div className="flex items-center justify-center p-4 sm:p-6">
                <img
                  src={asset_image_url}
                  alt={public_title || 'Certificate document'}
                  className="max-h-[70vh] w-auto max-w-full object-contain rounded-md shadow-sm bg-white"
                  data-testid="lightbox-image"
                />
              </div>
            ) : hasPdf ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <FileText className="h-10 w-10 text-slate-400 mb-3" aria-hidden />
                <p className="text-sm text-slate-600 mb-4">
                  {labels.pdfNotice || 'This certificate is only available as a PDF document.'}
                </p>
                <a
                  href={asset_pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3BA4C5] hover:text-[#2b8fae]"
                  data-testid="lightbox-open-pdf"
                >
                  {labels.openPdf || 'Open PDF'}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <ImageOff className="h-10 w-10 text-slate-300 mb-3" aria-hidden />
                <p className="text-sm text-slate-500">
                  {isInProgress
                    ? (labels.noAssetPending
                       || 'The certificate document will be published here once the authorization is granted.')
                    : (labels.noAsset || 'A copy of this certificate has not been published yet.')}
                </p>
              </div>
            )}
          </div>

          {(description || certificate_number || issue_date || valid_until || scope) && (
            <div className="px-5 py-4 border-t border-slate-200 bg-white grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
              {certificate_number && (
                <div>
                  <span className="text-slate-500">{labels.certNumber || 'Certificate no.'}: </span>
                  <span className="text-slate-800 font-medium">{certificate_number}</span>
                </div>
              )}
              {scope && (
                <div>
                  <span className="text-slate-500">{labels.scope || 'Scope'}: </span>
                  <span className="text-slate-800">{scope}</span>
                </div>
              )}
              {issue_date && (
                <div>
                  <span className="text-slate-500">{labels.issueDate || 'Issued'}: </span>
                  <span className="text-slate-800">{issue_date}</span>
                </div>
              )}
              {valid_until && (
                <div>
                  <span className="text-slate-500">{labels.validUntil || 'Valid until'}: </span>
                  <span className="text-slate-800">{valid_until}</span>
                </div>
              )}
              {description && (
                <div className="sm:col-span-2 text-slate-600 leading-relaxed pt-1">
                  {description}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CertificateLightbox;
